import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { __ } from '@wordpress/i18n';
import { isEmpty } from 'ramda';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

// store
import { useStoreValue, storeSet } from '../../store';

// components
import ChatMessage from './components/ChatMessage';
import SendIcon from '../../icons/SendIcon';
import ButtonSendChatMessage from '../ButtonWrapper/components/ButtonSendChatMessage';

const LOAD_MORE_THRESHOLD_PX = 150;

const Chat = ({ type = 'TEAM' }) => {
    const { projectId } = useParams();
    const [message, setMessage] = useState('');
    const [page, setPage] = useState(0);
    const [allItems, setAllItems] = useState([]);

    const osRef = useRef(null);
    const pageRef = useRef(0);
    const totalRef = useRef(0);
    const allItemsLengthRef = useRef(0);
    const isLoadingMoreRef = useRef(false);
    const shouldScrollBottomRef = useRef(false);
    const textareaRef = useRef(null);

    const messages = useStoreValue('messages');
    const messagesLoading = useStoreValue('messagesLoading');

    // Reset accumulated state on type/project change
    useEffect(() => {
        pageRef.current = 0;
        setPage(0);
        setAllItems([]);
        totalRef.current = 0;
        allItemsLengthRef.current = 0;
        isLoadingMoreRef.current = false;
        shouldScrollBottomRef.current = true;
    }, [projectId, type]);

    // Fetch when page, type or project changes
    useEffect(() => {
        if (projectId) {
            storeSet('fetchMessages', projectId, { type, pageId: page });
        }
    }, [projectId, type, page]);

    // Accumulate items when store updates
    useEffect(() => {
        if (!messages?.items) return;

        const newItems = [...messages.items].reverse(); // API returns newest-first; reverse to oldest-first

        if (pageRef.current === 0) {
            setAllItems(newItems);
        } else {
            setAllItems(prev => [...newItems, ...prev]); // prepend older messages
        }

        totalRef.current = messages.total ?? 0;
        isLoadingMoreRef.current = false;
    }, [messages]);

    // Keep length ref in sync for use in scroll handler
    useEffect(() => {
        allItemsLengthRef.current = allItems.length;
    }, [allItems]);

    // Scroll to bottom after initial load / send
    useEffect(() => {
        if (shouldScrollBottomRef.current && allItems.length > 0 && osRef.current) {
            const { viewport } = osRef.current.elements();
            viewport.scrollTop = viewport.scrollHeight;
            shouldScrollBottomRef.current = false;
        }
    }, [allItems]);

    const handleInitialized = useCallback((instance) => {
        osRef.current = instance;
    }, []);

    const handleScroll = useCallback((instance) => {
        if (isLoadingMoreRef.current) return;
        const el = instance.elements().scrollEventElement;
        if (el.scrollTop < LOAD_MORE_THRESHOLD_PX && allItemsLengthRef.current < totalRef.current) {
            isLoadingMoreRef.current = true;
            const nextPage = pageRef.current + 1;
            pageRef.current = nextPage;
            setPage(nextPage);
        }
    }, []);

    const handleTextareaChange = (e) => {
        setMessage(e.target.value);
        const el = e.target;
        el.style.height = 'auto';
        const maxHeight = parseFloat(getComputedStyle(el).maxHeight);
        el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
    };

    const handleSubmitChatMessage = () => {
        if (!message || isEmpty(message)) return;

        storeSet('sendMessage', projectId, { body: message }, type);
        setMessage('');

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        // Reset to page 0 so post-send refetch lands as a fresh page 0
        pageRef.current = 0;
        shouldScrollBottomRef.current = true;
        setAllItems([]);
        if (page !== 0) {
            setPage(0);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmitChatMessage();
        }
    };

    return (
        <div className="appChat">
            <OverlayScrollbarsComponent
                element="div"
                className="appChat__inner"
                options={{ scrollbars: { autoHide: 'never' } }}
                events={{ initialized: handleInitialized, scroll: handleScroll }}
                defer
            >
                {messagesLoading && pageRef.current === 0 && (
                    <div className="appChat__loading">{__('Loading...', 'soundbetween')}</div>
                )}
                {allItems.map(o => <ChatMessage key={o.id} data={o} />)}
            </OverlayScrollbarsComponent>

            <div className="appChat__chatSendWrapper">
                <textarea
                    ref={textareaRef}
                    name="message"
                    value={message}
                    rows={1}
                    maxLength={2000}
                    onChange={handleTextareaChange}
                    onKeyDown={handleKeyDown}
                />
                <ButtonSendChatMessage disabled={!message || isEmpty(message)} onClick={handleSubmitChatMessage}>
                    <SendIcon/>
                </ButtonSendChatMessage>
            </div>
        </div>
    );
};

export default Chat;
